import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchECACReport,
    patchBooking,
    resetECACReport,
    resetPatchBookingStatus,
    selectECACReportData,
    selectECACReportStatus,
    selectECACReportError,
    selectPatchBookingStatus,
} from 'redux/slices/sla-reports/ecac-report';
import { setToast, setShowToast } from '_redux/slices/timeline/timeline-toast';
import ReportHeader from './report-header';
import TerminalReceptionSummary from './terminal-reception-summary';
import TerminalReceptionRawDataTable from './terminal-reception-raw-data-table';

const ECACReport = ({ dateRange, iata, runReportRef, onRunLoadingChange, hideBreakdown }) => {
    const [overrideRow, setOverrideRow] = useState(null);
    const dispatch = useDispatch();
    const data = useSelector(selectECACReportData);
    const status = useSelector(selectECACReportStatus);
    const error = useSelector(selectECACReportError);
    const patchStatus = useSelector(selectPatchBookingStatus);
    const isLoading = status === 'loading';
    const isPatching = patchStatus === 'loading';

    const handleRunReport = useCallback(() => {
        const startDate = dateRange?.[0];
        const endDate = dateRange?.[1];

        if (!startDate || !endDate || !iata) return;

        const toLocalDateString = (d) => { const dt = new Date(d); return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`; };
        const formattedStartDate = toLocalDateString(startDate);
        const formattedEndDate = toLocalDateString(endDate);

        dispatch(
            fetchECACReport({
                startDate: formattedStartDate,
                endDate: formattedEndDate,
                iata,
                format: 'kpi',
                hideBreakdown,
            })
        );
    }, [dispatch, dateRange, iata, hideBreakdown]);

    useEffect(() => {
        return () => {
            dispatch(resetECACReport());
        };
    }, [dispatch]);

    useEffect(() => {
        if (patchStatus === 'succeeded') {
            dispatch(setToast({ message: 'Booking updated successfully', statusType: 'success' }));
            dispatch(setShowToast(true));
            dispatch(resetPatchBookingStatus());
            setOverrideRow(null);
            const toLocalDateString = (d) => { const dt = new Date(d); return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`; };
            const startDate = dateRange?.[0] ? toLocalDateString(dateRange[0]) : null;
            const endDate = dateRange?.[1] ? toLocalDateString(dateRange[1]) : null;
            if (startDate && endDate && iata) {
                dispatch(fetchECACReport({ startDate, endDate, iata, format: 'kpi', hideBreakdown }));
            }
        }
    }, [patchStatus, dispatch, dateRange, iata, hideBreakdown]);

    useEffect(() => {
        if (patchStatus === 'failed') {
            dispatch(setToast({ message: 'Failed to update booking', statusType: 'failed' }));
            dispatch(setShowToast(true));
            dispatch(resetPatchBookingStatus());
        }
    }, [patchStatus, dispatch]);

    useEffect(() => {
        if (runReportRef) runReportRef.current = handleRunReport;
        return () => { if (runReportRef) runReportRef.current = null; };
    }, [handleRunReport, runReportRef]);

    useEffect(() => {
        if (onRunLoadingChange) onRunLoadingChange(isLoading);
    }, [isLoading, onRunLoadingChange]);

    const dailyReports = data?.dailyReports ?? [];
    const isMultiDay = (data?.dateRange?.length ?? 0) > 1;
    const useKpiFormat = data?.format === 'kpi';
    const [includeNoData, setIncludeNoData] = useState(true);

    return (
        <div>
            <ReportHeader
                onRunReport={handleRunReport}
                isLoading={isLoading}
                hideRunButton={!!runReportRef}
                checkboxes={[
                    {
                        label: 'Include no-data rows (passengers who couldn\'t be qualified)',
                        checked: includeNoData,
                        onChange: (e) => setIncludeNoData(e.target.checked),
                    },
                ]}
            />

            {status === 'failed' && (
                <p style={{ color: '#991b1b', fontSize: '13px' }}>Error loading Terminal Reception report: {error}</p>
            )}

            {data && (
                <>
                    {useKpiFormat ? (
                        <>
                            <TerminalReceptionSummary
                                dailyReports={dailyReports}
                                overallSummary={isMultiDay ? data.summary : null}
                            />
                            {!hideBreakdown && (
                                <TerminalReceptionRawDataTable
                                    dailyReports={dailyReports}
                                    timeColumnKey="departureTime"
                                    timeColumnLabel="Departure Time"
                                    locationColumnKey="callPoint"
                                    locationColumnLabel="Call Point"
                                    includeNoData={includeNoData}
                                    overrideRow={overrideRow}
                                    onOverrideRowChange={setOverrideRow}
                                    onPatchBooking={(row, pass, reason, kpiKey) => {
                                        if (row?.bookingId) {
                                            dispatch(patchBooking({
                                                bookingId: row.bookingId,
                                                patch: {
                                                    ecacReportOverride: {
                                                        [kpiKey]: { pass, passReason: reason },
                                                    },
                                                },
                                            }));
                                        }
                                    }}
                                    isPatching={isPatching}
                                />
                            )}
                        </>
                    ) : (
                        <p style={{ fontSize: '14px', color: '#6b7280' }}>
                            Bucket format not supported in this view. Use format=kpi.
                        </p>
                    )}
                </>
            )}
        </div>
    );
};

export default ECACReport;
